import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'app/shared/services/api.service';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-client-pipeline-add',
  templateUrl: './client-pipeline-add.component.html',
  styleUrls: ['./client-pipeline-add.component.scss', '../../../../../../assets/sass/libs/select.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ClientPipelineAddComponent implements OnInit {
  pipelineForm: FormGroup;
  pipelineFormSubmitted = false;
  isEditMode = false;
  clientPipelineId: string = null;
  
  // Dropdown data
  statusList = [
    { _id: 'extracted', name: 'Extracted' },
    { _id: 'moved to erp', name: 'Moved to ERP' }
  ];
  sourceList = [];
  programTagsList = [];
  courseList = [];
  eventsList = [];
  
  // Selected values
  selectedStatus = '';
  selectedSources = [];
  selectedProgramTags = [];
  selectedCourses = [];
  selectedEvents = [];
  selectedDate = '';
  selectedMonth = '';
  selectedYear = '';

  constructor(
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private apiService: ApiService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadDropdownData();
    this.checkEditMode();
  }

  checkEditMode(): void {
    this.activatedRoute.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.clientPipelineId = params['id'];
        this.loadClientPipelineData();
      }
    });
  }

  loadClientPipelineData(): void {
    this.showSpinner();
    
    this.apiService.getClientPipelineById(this.clientPipelineId).subscribe(
      (res: any) => {
        this.hideSpinner();
        if (res?.statusCode === 200 || res?.data) {
          const data = res.data || res;
          this.populateForm(data);
        } else {
          this.toastr.error('Failed to load client pipeline data');
          this.router.navigate(['/admin/client-pipelines']);
        }
      },
      (error) => {
        this.hideSpinner();
        this.toastr.error('Error loading client pipeline data');
        this.router.navigate(['/admin/client-pipelines']);
      }
    );
  }

  populateForm(data: any): void {
    // Populate form fields
    this.pipelineForm.patchValue({
      clientName: data.clientName || '',
      clientEmail: data.clientEmail || '',
      clientPhone: data.clientPhone || '',
      interested_in: data.interested_in || '',
      notes: data.notes || ''
    });

    // Populate selected values
    this.selectedStatus = data.status || '';
    this.selectedSources = data.source || [];
    this.selectedProgramTags = data.programtags || [];
    this.selectedCourses = data.course_ids || [];
    this.selectedEvents = data.events_id || [];

    // Populate timeline date
    if (data.timelinetags?.month && data.timelinetags?.year) {
      const monthIndex = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ].indexOf(data.timelinetags.month);
      
      if (monthIndex !== -1) {
        const month = (monthIndex + 1).toString().padStart(2, '0');
        this.selectedDate = `${data.timelinetags.year}-${month}`;
        this.selectedMonth = data.timelinetags.month;
        this.selectedYear = data.timelinetags.year;
      }
    }
  }

  initializeForm(): void {
    this.pipelineForm = this.formBuilder.group({
      clientName: ['', Validators.required],
      clientEmail: ['', [Validators.required, Validators.email]],
      clientPhone: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]],
      interested_in: ['', Validators.required],
      notes: ['']
    });
  }

  get pf() {
    return this.pipelineForm.controls;
  }

  loadDropdownData(): void {
    // Load courses
    this.apiService.getCourseList(1, 100).subscribe(
      (res: any) => {
        if (res?.data?.items) {
          this.courseList = res.data.items.map(item => ({
            _id: item._id,
            name: item.title
          }));
        }
      },
      (error) => {
        this.toastr.error('Failed to load courses');
      }
    );

    // Load events
    this.apiService.getEventList(1, 100).subscribe(
      (res: any) => {
        if (res?.data?.items) {
          this.eventsList = res.data.items.map(item => ({
            _id: item._id,
            name: item.name
          }));
        }
      },
      (error) => {
        this.toastr.error('Failed to load events');
      }
    );
  }

  onStatusChange(event: any): void {
    // Status change handler
  }

  onCoursesChange(event: any): void {
    // Courses change handler
  }

  onEventsChange(event: any): void {
    // Events change handler
  }

  onDateChange(event: any): void {
    if (this.selectedDate) {
      const date = new Date(this.selectedDate + '-01');
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      this.selectedMonth = monthNames[date.getMonth()];
      this.selectedYear = date.getFullYear().toString();
    }
  }

  submitForm(): void {
    this.pipelineFormSubmitted = true;

    // Validate required fields
    if (this.pipelineForm.invalid) {
      this.toastr.error('Please fill all required fields!');
      return;
    }

    if (!this.selectedStatus) {
      this.toastr.error('Please select a status!');
      return;
    }

    // Prepare request body
    const body = {
      clientName: this.pipelineForm.value.clientName,
      clientEmail: this.pipelineForm.value.clientEmail,
      clientPhone: this.pipelineForm.value.clientPhone,
      interested_in: this.pipelineForm.value.interested_in,
      notes: this.pipelineForm.value.notes,
      status: this.selectedStatus,
      source: this.selectedSources,
      timelinetags: {
        month: this.selectedMonth,
        year: this.selectedYear
      },
      programtags: this.selectedProgramTags,
      course_ids: this.selectedCourses,
      events_id: this.selectedEvents
    };

    this.showSpinner();

    if (this.isEditMode) {
      this.updateClientPipeline(body);
    } else {
      this.addClientPipeline(body);
    }
  }

  addClientPipeline(body: any): void {
    this.apiService.addClientPipeline(body).subscribe(
      (res: any) => {
        this.hideSpinner();
        if (res?.statusCode === 200 || res?.isSuccess === true) {
          this.toastr.success(res?.message || 'Client pipeline added successfully!');
          this.router.navigate(['/client-pipelines']);
        } else {
          this.toastr.error(res?.error || 'Failed to add client pipeline');
        }
      },
      (error) => {
        this.hideSpinner();
        this.toastr.error('An error occurred while adding client pipeline');
      }
    );
  }

  updateClientPipeline(body: any): void {
    this.apiService.updateClientPipeline(this.clientPipelineId, body).subscribe(
      (res: any) => {
        this.hideSpinner();
        if (res?.statusCode === 200 || res?.isSuccess === true) {
          this.toastr.success(res?.message || 'Client pipeline updated successfully!');
          this.router.navigate(['/client-pipelines']);
        } else {
          this.toastr.error(res?.error || 'Failed to update client pipeline');
        }
      },
      (error) => {
        this.hideSpinner();
        this.toastr.error('An error occurred while updating client pipeline');
      }
    );
  }

  back(): void {
    this.router.navigate(['/admin/client-pipelines']);
  }

  private showSpinner(): void {
    this.spinner.show(undefined, {
      type: 'ball-triangle-path',
      size: 'medium',
      bdColor: 'rgba(0, 0, 0, 0.8)',
      color: '#fff',
      fullScreen: true
    });
  }

  private hideSpinner(): void {
    this.spinner.hide();
  }
}

